// Les fenêtres des exercices, ouvertes d'un clic dans le manuel : l'exercice
// compilé seul (/web/exercice.typ), avec la graine de son choix et, au besoin,
// son corrigé. Elles flottent au-dessus du manuel, se déplacent par leur barre de
// titre, se redimensionnent par leurs bords. Un double-clic sur la barre range la
// fenêtre dans la moitié ou le quart de l'écran le plus proche ; un autre, ou un
// glisser, lui rend sa taille d'avant. Les boutons de la barre affichent le
// corrigé et déplient les réglages de la graine, repliés à l'ouverture.
//
// Le balisage d'une fenêtre est un modèle de la page (<template id="modele-fenetre">).

import { nomExercice, type Outils, texteErreurs } from './compilateur.ts';
import type { Chapitre, Exercice, Manuel } from './types.ts';

const modèle = document.getElementById('modele-fenetre') as HTMLTemplateElement;

const borner = (v: number, min: number, max: number) => Math.min(Math.max(v, min), Math.max(min, max));

// Taille minimale d'une fenêtre, en pixels.
const LARGEUR_MIN = 280;
const HAUTEUR_MIN = 160;

// Écart entre les fenêtres rangées, et avec les bords de l'écran, en pixels.
const ÉCART = 8;

// Où ranger une fenêtre : une moitié (w, e) ou un quart (nw, ne, sw, se), comme
// les bords des poignées.
type Zone = 'w' | 'e' | 'nw' | 'ne' | 'sw' | 'se';

// Suit le pointeur depuis `ev` (un pointerdown) jusqu'à ce qu'on le relâche, en
// appelant `bouger(dx, dy)` avec le déplacement depuis le départ.
function glisser(ev: PointerEvent, bouger: (dx: number, dy: number) => void) {
  ev.preventDefault();
  const cible = ev.currentTarget as HTMLElement;
  const suivre = (e: PointerEvent) => bouger(e.clientX - ev.clientX, e.clientY - ev.clientY);
  cible.setPointerCapture(ev.pointerId);
  cible.addEventListener('pointermove', suivre);
  cible.addEventListener('lostpointercapture', () => cible.removeEventListener('pointermove', suivre), { once: true });
}

// Rend `ouvrir(chapitre, exercice, origine)`, qui ouvre un exercice du manuel dans
// une nouvelle fenêtre, ou ramène au premier plan celle où il est déjà ouvert ;
// `origine` est l'élément cliqué, qui reprend le focus à la fermeture.
interface Fenêtre {
  élément: HTMLElement;
  placer(x: number, y: number): void;
  rangée(): Zone | null;
  ranger(zone: Zone): void;
  fermer(): void;
}

// La zone de l'écran où l'on range les fenêtres : sous l'en-tête du manuel, qui
// reste collé en haut pendant le défilement.
function surface() {
  const tête = document.querySelector('.manuel-tete')?.getBoundingClientRect();
  const haut = Math.max(0, tête?.bottom ?? 0);
  return { haut, largeur: innerWidth, hauteur: innerHeight - haut };
}

export function fenêtreExercice(outils: Outils, manuel: Manuel) {
  const ouvertes = new Map<Exercice, Fenêtre>();
  let plan = 10; // z-index de la fenêtre au premier plan

  function premierPlan(f: Fenêtre) {
    f.élément.style.zIndex = String(++plan);
  }

  // Échap ferme la fenêtre où se trouve le focus, à défaut celle du premier plan.
  window.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Escape' || !ouvertes.size) return;
    const toutes = [...ouvertes.values()];
    const f =
      toutes.find((f) => f.élément.contains(document.activeElement)) ??
      toutes.reduce((a, b) => (Number(a.élément.style.zIndex) > Number(b.élément.style.zIndex) ? a : b));
    f.fermer();
  });

  window.addEventListener('resize', () => {
    for (const f of ouvertes.values()) {
      const zone = f.rangée();
      if (zone) f.ranger(zone);
      else f.placer(f.élément.offsetLeft, f.élément.offsetTop);
    }
  });

  function créer(chapitre: Chapitre, exercice: Exercice, origine: HTMLElement): Fenêtre {
    const élément = modèle.content.firstElementChild?.cloneNode(true) as HTMLElement;
    const $ = <E extends HTMLElement = HTMLElement>(classe: string) => élément.querySelector(`.fenetre-${classe}`) as E;
    const barre = $('barre');
    const titre = $('titre');
    const graine = $<HTMLInputElement>('graine');
    const corrigé = $<HTMLButtonElement>('corrige');
    const état = $('etat');
    const rendu = $('rendu');
    const réglages = $<HTMLFormElement>('reglages');
    const options = $<HTMLButtonElement>('options');
    const f: Fenêtre = { élément, placer, rangée: () => zoneRangée, ranger, fermer };

    titre.id = `fenetre-titre-${chapitre.num}-${exercice.n}`;
    élément.setAttribute('aria-labelledby', titre.id);
    élément.style.setProperty('--theme', manuel.couleurs[chapitre.thème] ?? 'var(--noir)');
    const badge = Object.assign(document.createElement('span'), {
      className: 'fenetre-badge',
      textContent: nomExercice(chapitre.num, exercice.n),
    });
    titre.replaceChildren(badge, exercice.titre);
    réglages.id = `fenetre-reglages-${chapitre.num}-${exercice.n}`;
    options.setAttribute('aria-controls', réglages.id);
    graine.value = String(exercice.graine);

    // ── Position ─────────────────────────────────────────────────────────────

    // La fenêtre reste dans l'écran, sa barre de titre au moins si elle est trop haute.
    function placer(x: number, y: number) {
      const { width } = élément.getBoundingClientRect();
      élément.style.left = `${borner(x, 0, innerWidth - width)}px`;
      élément.style.top = `${borner(y, 0, innerHeight - barre.offsetHeight)}px`;
    }

    // Elle s'ouvre du côté de l'écran opposé à l'exercice cliqué, pour ne pas le
    // cacher, un peu décalée des fenêtres déjà ouvertes.
    function placerPrès() {
      const { width } = élément.getBoundingClientRect();
      const r = origine.getBoundingClientRect();
      const àDroite = r.left + r.width / 2 < innerWidth / 2;
      const marge = 24;
      const décalage = 28 * ((ouvertes.size - 1) % 8);
      placer(àDroite ? innerWidth - width - marge - décalage : marge + décalage, 80 + décalage);
    }

    // ── Rangement ────────────────────────────────────────────────────────────

    let zoneRangée: Zone | null = null;
    let avant = { left: '', top: '', width: '', height: '' }; // styles d'avant le rangement

    // La zone la plus proche du centre de la fenêtre : la moitié gauche ou droite,
    // ou un quart si le centre est dans le tiers haut ou bas de l'écran.
    function zoneProche(): Zone {
      const r = élément.getBoundingClientRect();
      const s = surface();
      const x = (r.left + r.width / 2) / s.largeur;
      const y = (r.top + r.height / 2 - s.haut) / s.hauteur;
      const côté = x < 0.5 ? 'w' : 'e';
      return y < 1 / 3 ? `n${côté}` : y > 2 / 3 ? `s${côté}` : côté;
    }

    function ranger(zone: Zone) {
      if (!zoneRangée) {
        const { left, top, width, height } = élément.style;
        avant = { left, top, width, height };
      }
      zoneRangée = zone;
      const s = surface();
      const largeur = Math.max(LARGEUR_MIN, (s.largeur - 3 * ÉCART) / 2);
      const hauteur = Math.max(HAUTEUR_MIN, zone.length === 1 ? s.hauteur - 2 * ÉCART : (s.hauteur - 3 * ÉCART) / 2);
      const x = zone.endsWith('w') ? ÉCART : s.largeur - ÉCART - largeur;
      const y = zone.startsWith('s') ? innerHeight - ÉCART - hauteur : s.haut + ÉCART;
      Object.assign(élément.style, { left: `${x}px`, top: `${y}px`, width: `${largeur}px`, height: `${hauteur}px` });
    }

    function restaurer() {
      zoneRangée = null;
      Object.assign(élément.style, avant);
      placer(élément.offsetLeft, élément.offsetTop);
    }

    // Le temps d'un double-clic, la fenêtre glisse jusqu'à sa nouvelle place.
    function animer() {
      élément.classList.add('rangement');
      setTimeout(() => élément.classList.remove('rangement'), 200);
    }

    élément.addEventListener('pointerdown', () => premierPlan(f), { capture: true });
    élément.addEventListener('focusin', () => premierPlan(f));
    barre.addEventListener('dblclick', (ev) => {
      if ((ev.target as Element).closest('button')) return;
      animer();
      if (zoneRangée) restaurer();
      else ranger(zoneProche());
    });
    // Une fenêtre rangée qu'on glisse reprend sa taille d'avant, le pointeur à la
    // même proportion de sa barre ; pas avant quelques pixels, pour laisser passer
    // le double-clic.
    barre.addEventListener('pointerdown', (ev) => {
      if (ev.button !== 0 || (ev.target as Element).closest('button')) return;
      const r = élément.getBoundingClientRect();
      let left = r.left;
      glisser(ev, (dx, dy) => {
        if (zoneRangée) {
          if (Math.hypot(dx, dy) < 4) return;
          restaurer();
          left = ev.clientX - ((ev.clientX - r.left) / r.width) * élément.offsetWidth;
        }
        placer(left + dx, r.top + dy);
      });
    });

    // Par un bord ou un coin : à droite (e), à gauche (w), en bas (s). Le bord
    // opposé reste en place ; la fenêtre ne sort pas de l'écran.
    for (const poignée of élément.querySelectorAll<HTMLElement>('.fenetre-poignee')) {
      const bord = poignée.dataset.bord ?? '';
      poignée.addEventListener('pointerdown', (ev) => {
        if (ev.button !== 0) return;
        zoneRangée = null;
        const r = élément.getBoundingClientRect();
        glisser(ev, (dx, dy) => {
          if (bord.includes('e')) {
            élément.style.width = `${borner(r.width + dx, LARGEUR_MIN, innerWidth - r.left)}px`;
          } else if (bord.includes('w')) {
            const largeur = borner(r.width - dx, LARGEUR_MIN, r.right);
            élément.style.width = `${largeur}px`;
            élément.style.left = `${r.right - largeur}px`;
          }
          if (bord.includes('s')) {
            élément.style.height = `${borner(r.height + dy, HAUTEUR_MIN, innerHeight - r.top)}px`;
          }
        });
      });
    }

    // Avec ou sans corrigé, réglages dépliés ou non, la fenêtre prend la hauteur
    // de son contenu, sans changer de largeur ; elle remonte au besoin pour tenir
    // dans l'écran. Rangée, elle garde sa place.
    function ajusterHauteur() {
      if (zoneRangée) return;
      élément.style.height = '';
      const r = élément.getBoundingClientRect();
      if (r.bottom > innerHeight) élément.style.top = `${Math.max(0, innerHeight - r.height)}px`;
    }

    // ── Compilation ──────────────────────────────────────────────────────────

    let enCours = false;
    let enAttente = false;
    let ajuster = false; // à la hauteur du contenu, au prochain rendu

    // Une seule compilation à la fois ; un réglage changé pendant qu'elle tourne
    // est pris en compte par une nouvelle compilation, à la fin.
    async function afficher() {
      if (enCours) {
        enAttente = true;
        return;
      }
      enCours = true;
      try {
        do {
          enAttente = false;
          await compiler();
        } while (enAttente && élément.isConnected);
      } finally {
        enCours = false;
      }
    }

    async function compiler() {
      const valeur = Number.parseInt(graine.value, 10);
      const àAjuster = ajuster;
      if (!Number.isFinite(valeur)) {
        état.textContent = 'La graine doit être un nombre entier.';
        return;
      }
      état.textContent = 'Compilation…';
      rendu.setAttribute('aria-busy', 'true');
      const { svg, erreurs } = await outils.compiler('/web/exercice.typ', {
        exercice: exercice.chemin,
        thèmes: manuel.thèmes,
        thème: chapitre.thème,
        chapitre: String(chapitre.num),
        numéro: String(exercice.n),
        graine: String(valeur),
        corrigé: corrigé.getAttribute('aria-pressed') === 'true' ? 'oui' : 'non',
        titre: 'non', // déjà dans la barre de la fenêtre
      });
      rendu.removeAttribute('aria-busy');
      if (erreurs) {
        état.textContent = 'La compilation a échoué.';
        rendu.replaceChildren(
          Object.assign(document.createElement('pre'), { className: 'erreurs', textContent: texteErreurs(erreurs) }),
        );
        return;
      }
      rendu.innerHTML = svg;
      état.textContent = '';
      if (àAjuster && ajuster) {
        ajuster = false;
        ajusterHauteur();
      }
    }

    // ── Réglages, fermeture ──────────────────────────────────────────────────

    function fermer() {
      const avaitLeFocus = élément.contains(document.activeElement);
      ouvertes.delete(exercice);
      élément.remove();
      origine.classList.remove('ouverte');
      if (avaitLeFocus) origine.focus({ preventScroll: true });
    }

    graine.addEventListener('input', afficher);
    corrigé.addEventListener('click', () => {
      const afficherCorrigé = corrigé.getAttribute('aria-pressed') !== 'true';
      corrigé.setAttribute('aria-pressed', String(afficherCorrigé));
      corrigé.title = afficherCorrigé ? 'Masquer le corrigé' : 'Afficher le corrigé';
      ajuster = true;
      afficher();
    });
    $('hasard').addEventListener('click', () => {
      graine.value = String(Math.floor(Math.random() * 1_000_000));
      afficher();
    });
    $('manuel').addEventListener('click', () => {
      graine.value = String(exercice.graine);
      afficher();
    });
    options.addEventListener('click', () => {
      const déplier = réglages.hidden;
      réglages.hidden = !déplier;
      options.setAttribute('aria-expanded', String(déplier));
      ajusterHauteur();
      if (déplier) graine.focus({ preventScroll: true });
    });
    $('fermer').addEventListener('click', fermer);
    réglages.addEventListener('submit', (ev) => ev.preventDefault());

    ouvertes.set(exercice, f);
    origine.classList.add('ouverte');
    document.body.append(élément);
    premierPlan(f);
    placerPrès();
    afficher();
    return f;
  }

  return function ouvrir(chapitre: Chapitre, exercice: Exercice, origine: HTMLElement) {
    const f = ouvertes.get(exercice) ?? créer(chapitre, exercice, origine);
    premierPlan(f);
    f.élément.focus({ preventScroll: true });
  };
}
